/**
 * DI-3-S4-FE (smoke F4) — the UPLOAD LANE: an approved set's unfinished
 * upload finishes beside the running drop.
 *
 * The single-runner rule as it stood (DI-3-D / DI-3-P8): the tab's ONE
 * `intakeRunner` holds ONE session (`RunnerState.sessionId`, one index, one
 * folder), the browser's ONE active session (`meta.activeSession`) is what it
 * restores, and `resumeInThisBrowser` refuses ("busy") while this tab's runner
 * holds another started session or another live session is active. So an
 * approved set whose upload was interrupted (a reload, a new drop replacing
 * the runner's session) could not finish while any newer drop was open — the
 * deadlock of F4 (370 of 527 sent, the server's 0, "resume this one once that
 * drop is done").
 *
 * The lane narrows the rule to "one running DROP per browser" (lock
 * one-drop-many-finishes): next to the drop slot, untouched, this module runs
 * the uploads of other sessions — never their creates, round trips or
 * `metadata-complete`, and never by making their session the active one (the
 * 2026-09-29 incident: the lane never calls `rememberActiveSession`).
 *
 *   admission  `laneAdmission`: a live session record, not the running drop's,
 *              every started set handed over (`running` — the browser's part
 *              except the upload is done), and at least one set whose upload
 *              is unfinished here AND that the WIRE says is approved
 *              (`approvedAt`, DI-3-S1 — the local record only bridges, so it
 *              does not count). Only those sets go into the lane;
 *   the folder per session, as today: the card's button asks for it on the
 *              click (`laneFolder.ts`); the files are re-indexed in an index
 *              of the lane's own with the hashes persisted at the round trip
 *              (DI-3-P2: a matching file is head-parsed only), the sets get
 *              their members back by their stable id;
 *   the upload DI-3-F's `UploadDriver`, unchanged — the ledger, the block
 *              queue (the tab-wide `UploadQueue` the drop's uploads use too:
 *              still four PUTs for the tab), the manifest, the retry pass —
 *              over a runner-shaped store of the lane's own, writing the
 *              session's record through a write chain of its own;
 *   the queue  ONE session at a time; others wait, ordered by the record's
 *              creation time;
 *   the claim  per lane session (`laneClaim:<sessionId>` in IndexedDB, the
 *              `RunnerClaim` protocol by age): a second tab of this browser
 *              does not run the same upload, and `resumeInThisBrowser` / the
 *              Upload page's `?da=` view refuse to make a lane-held session
 *              active (`laneBusy`).
 *
 * DI-3-S8-FE (smoke F7) — the lane claims only what it can drive. A tab
 * takes a session's claim only once it holds the files (`canDrive` →
 * "files": the click's folder, its index) or a remembered handle whose
 * permission is already granted ("handle": read without a gesture, before the
 * claim); "none" (no files, no granted handle, or a record of another user
 * signed in here) is refused by `enqueue` (`"needs_folder"`) and writes NO
 * claim. A tab that holds the files but finds another tab's fresh claim says
 * so and offers "Take over in this tab": `RunnerClaim.takeOver()` (the lane's
 * steal; the drop slot never calls it) writes the claim with this tab's id;
 * the owner's next heartbeat reads the foreign id, CLOSES its gates (no
 * further block, call or record write), waits for its in-flight requests,
 * acknowledges (`laneYield:<sessionId>`) and ends with "Taken over by another
 * tab."; the stealer sends nothing before that acknowledgement or
 * `TAKEOVER_WAIT_MS` (one heartbeat + the in-flight bound). A remembered
 * handle whose permission goes while the lane sends ends the entry with "The
 * folder is no longer readable here — resume to choose it again." and
 * releases the claim.
 *
 * DI-3-S11-FE — a fourth way to HOLD the files: "root" — no files, no
 * granted handle of the record, but the record's folder found by name under a
 * granted field-data root (`rootFolderFor`, no gesture). Admitted exactly
 * like "handle": the folder is read BEFORE the claim (none → the entry ends
 * without a claim), and the root's permission is watched while the lane
 * sends. The take-over handshake is unchanged.
 *
 * DI-3-S16-FE (smoke R2-F4 / R2-F5) — a fifth: "set-folder" — the folder
 * picked on the card for one of the record's sets (`handle:set:<daId>`,
 * DI-3-P13), granted; between "handle" and "root" (the one order of
 * `files/rootFolder.ts`), read before the claim like them
 * (`readSetFolder`), its permission watched while the lane sends. And an
 * entry the card queued BY ITSELF (`auto`, no click) that finds another
 * tab's claim ends silently — no busy line, no take-over offer: the busy line
 * stays a click's answer (S8), and the automatic start never takes over.
 *
 * M5-NODE-01 — the escape hatch ("Upload images…", `escapeHatch/`) rides
 * the lane: its record holds ONE set with the reason (`uploadReason`), which
 * the admission takes without an approval on the wire (a withheld set may be
 * uploaded before or after Approve) and every session open sends.
 *
 * Resume skips what was sent: the ledger (DI-3-F) — a file committed with
 * the same name, size and MD5 is not sent again. No HEAD on the blob: the
 * upload SAS is `cw` (create + write, the gateway's `upload-sas.ts`), which
 * does not authorise reading a blob's properties; a file without a ledger
 * entry is sent again (a re-PUT of a committed blob replaces it with the
 * same bytes) and the ingest's verification stays the final word.
 */
import { createStore, useStore, type StoreApi } from "zustand";
import {
  createIndexStore,
  type IndexRow,
  type IndexState,
} from "../indexStore.js";
import { IndexClient, type WorkerLike } from "../parse/workerClient.js";
import {
  filesFromDirectoryHandle,
  type CollectResult,
  type DirectoryHandleLike,
} from "../files/dropToFiles.js";
import { permissionOf, recall, ROOT_KEY } from "../files/handleStore.js";
import { rootFolderFor, setFolderOf } from "../files/rootFolder.js";
import type { HashResult } from "../parse/types.js";
import {
  getSession,
  updateCluster,
  updateSession,
  type ClusterIntake,
  type SessionRecord,
} from "../session/sessionStore.js";
import { deleteMeta, getMeta, setMeta, updateMeta } from "../session/db.js";
import {
  HEARTBEAT_MS,
  RunnerClaim,
  claimable,
  type RunnerClaimRecord,
} from "./claim.js";
import {
  createRunnerStore,
  EMPTY_RUNNER,
  patchSet,
  type RunnerSet,
  type RunnerState,
} from "./runnerStore.js";
import { reached } from "./phases.js";
import { folderMembers, groupForDecide, proposalSets } from "./sets.js";
import { fileKey } from "../add/diff.js";
import { knownForFolder, knownHashesOf } from "../parse/hashKey.js";
import { UploadDriver, type DriverDeps } from "./uploadDriver.js";
import { REAL_UPLOAD_API, type UploadApi } from "../upload/api.js";
import type { FetchLike } from "../upload/blockUpload.js";
import type { UploadQueue } from "../upload/queue.js";
import { uploadFinished, uploadNeedsFiles } from "../upload/state.js";
import { intakeRunner, runnerSetFromCluster } from "./intakeRunner.js";

// ── admission (pure) ───────────────────────────────────────────────────────

export type LaneRefusal =
  /** No record, no started set, or the session ended (DI-3-P6). */
  | "not-live"
  /** The record IS the running drop's: the drop slot finishes it. */
  | "running-drop"
  /** A started set still needs the browser for more than its upload. */
  | "not-upload-only"
  /** Nothing unfinished to upload in this record. */
  | "nothing-to-upload"
  /** Unfinished uploads, but no such set is approved on the wire. */
  | "not-approved";

export type LaneAdmission =
  | {
      kind: "admitted";
      /** The sets the lane uploads: unfinished here AND approved on the wire. */
      clusterIds: string[];
      /** One of them still has bytes or a manifest to send (the folder is needed). */
      needsFiles: boolean;
    }
  | { kind: "refused"; reason: LaneRefusal };

/** The approval the wire carries for an acquisition (ISO), else null / undefined. */
export type WireApproval = (daId: string) => string | null | undefined;

/**
 * A started set of this record whose upload this browser has not finished:
 * an uploading workflow (`uploadChoice` set), an acquisition, an upload not
 * `present` (`failed` counts — the lane starts it again, as "Retry the
 * upload" does).
 */
export function uploadUnfinishedHere(c: ClusterIntake): boolean {
  return (
    c.phase !== undefined &&
    Boolean(c.dataAcquisitionId) &&
    c.uploadChoice !== null &&
    c.uploadChoice !== undefined &&
    c.upload !== undefined &&
    c.upload.phase !== "present" &&
    // DI-3-S68 T1 — the server refused it for good (approved / withdrawn):
    // the lane drops the set, never retries it
    !c.upload.ended
  );
}

function refused(reason: LaneRefusal): LaneAdmission {
  return { kind: "refused", reason };
}

/**
 * May this record's upload run in the lane? (story §1.1 — approved on the
 * wire, the remaining work only the upload, never the running drop's own.)
 */
export function laneAdmission(
  rec: SessionRecord | null,
  approvedAt: WireApproval,
  dropSessionId: string | null,
): LaneAdmission {
  const started = (rec?.clusters ?? []).filter((c) => c.phase !== undefined);
  if (!rec || started.length === 0 || rec.endedAt !== undefined)
    return refused("not-live");
  if (dropSessionId !== null && rec.sessionId === dropSessionId)
    return refused("running-drop");
  if (started.some((c) => !reached(c.phase ?? "decided", "running")))
    return refused("not-upload-only");
  const unfinished = started.filter(uploadUnfinishedHere);
  if (unfinished.length === 0) return refused("nothing-to-upload");
  // M5-NODE-01 — an escape-hatch upload ("Upload images…", the reason
  // given) is the pilot's own act on a withheld set, before or after Approve:
  // it needs no approval on the wire to run.
  // DI-3-S40 (B′) — nor does an "upload now" set (N2: "now" runs once the
  // set is synced, never waiting for Approve): "Drop the next folder" hands
  // a handed-over session's unfinished "now" uploads to the lane. An "after
  // verification" set still waits for the wire's approval.
  const approved = unfinished.filter(
    (c) =>
      Boolean(c.uploadReason) ||
      c.uploadChoice === "now" ||
      typeof approvedAt(c.dataAcquisitionId ?? "") === "string",
  );
  if (approved.length === 0) return refused("not-approved");
  return {
    kind: "admitted",
    clusterIds: approved.map((c) => c.clusterId),
    needsFiles: approved.some(
      (c) => c.upload?.phase === "failed" || uploadNeedsFiles(c.upload),
    ),
  };
}

// ── can this tab drive the upload? (pure — DI-3-S8-FE) ─────────────────────

/**
 * How this tab can send a session's bytes: "files" — it holds the folder's
 * files (the click's pick, its index); "handle" — the session's remembered
 * folder handle answers `queryPermission` "granted" (read without a gesture);
 * DI-3-S16-FE "set-folder" — neither of those, but a folder picked on the
 * card for one of the record's sets is granted (no gesture);
 * DI-3-S11-FE "root" — none of those, but the record's folder sits under a
 * granted field-data root (found by name — DI-3-S16-FE up to three levels
 * down, or the path recorded at drop time — no gesture);
 * "none" — none of them, or the record is another user's (a second account
 * signed in in a tab of the same browser storage). Only "files" / "handle" /
 * "set-folder" / "root" may claim.
 */
export type LaneDrive = "files" | "handle" | "set-folder" | "root" | "none";

/** The remembered handle's permission now (`null` = no handle stored). */
export type HandlePermission = PermissionState | "unknown" | null;

export interface LaneSubject {
  handleKey?: string | undefined;
  /** The set was created by another user than the one signed in here. */
  foreign?: boolean | undefined;
}

export function canDrive(
  session: LaneSubject,
  files: CollectResult | null,
  handlePermission: HandlePermission,
  /** DI-3-S11-FE — the record's folder resolved under a granted root. */
  underRoot = false,
  /** DI-3-S16-FE — a folder picked on the card for one of its sets, granted. */
  setFolder = false,
): LaneDrive {
  if (session.foreign === true) return "none";
  if (files !== null && files.files.length > 0) return "files";
  if (session.handleKey && handlePermission === "granted") return "handle";
  if (setFolder) return "set-folder";
  if (underRoot) return "root";
  return "none";
}

// ── the lane ───────────────────────────────────────────────────────────────

export type LanePhase =
  | "queued"
  /** DI-3-S8-FE — the claim is taken; waiting for the other tab to yield. */
  | "taking-over"
  | "reading"
  | "uploading"
  | "error";

export interface LaneEntry {
  sessionId: string;
  folderName: string;
  /** The record's creation time: the queue's order. */
  createdAt: string;
  clusterIds: string[];
  phase: LanePhase;
  /** Why the entry stopped (phase `error`): the card's words. */
  error: string | null;
  /** DI-3-S8-FE — how this tab drives it (`canDrive`, never "none"). */
  drive?: "files" | "handle" | "set-folder" | "root" | undefined;
  /**
   * DI-3-S16-FE — queued by the card by itself (no click): another tab's
   * claim ends it silently (the entry goes), never a busy line.
   */
  auto?: boolean | undefined;
  /**
   * DI-3-S8-FE — stopped on another tab's claim (`LANE_OTHER_TAB`) while THIS
   * tab holds the files: the busy line offers "Take over in this tab".
   */
  canTakeOver?: boolean | undefined;
}

/** DI-3-S8-FE — the `meta` store the claims and the yield go through. */
export interface LaneMeta {
  get: <T>(key: string) => Promise<T | undefined>;
  update: <T>(
    key: string,
    fn: (cur: T | undefined) => T | undefined,
  ) => Promise<T | undefined>;
  set: (key: string, value: unknown) => Promise<void>;
  delete: (key: string) => Promise<void>;
}

const IDB_META: LaneMeta = {
  get: getMeta,
  update: updateMeta,
  set: setMeta,
  delete: deleteMeta,
};

/** The owner's acknowledgement of a take-over (`laneYield:<sessionId>`). */
export interface LaneYield {
  from: string;
  to: string;
  at: number;
}

export interface EnqueueOptions {
  /** The set was created by another user than the one signed in here. */
  foreign?: boolean | undefined;
  /** The remembered handle's permission, read without a gesture. */
  handlePermission?: HandlePermission | undefined;
  /** The files came through the remembered handle: watch its permission. */
  via?: "handle" | "picker" | "input" | "root" | "set-folder" | undefined;
  /**
   * DI-3-S11-FE — the record's folder resolved under a granted root
   * (`rootFolderFor` → "resolved"), read without a gesture.
   */
  underRoot?: boolean | undefined;
  /**
   * DI-3-S16-FE — a folder picked on the card for one of the record's sets
   * is granted; `setFolderKey` = its handle key (watched while sending).
   */
  setFolder?: boolean | undefined;
  setFolderKey?: string | undefined;
  /** DI-3-S16-FE — the card queued it by itself (no click). */
  auto?: boolean | undefined;
}

export interface LaneState {
  /** The active entry first, then the queue by creation time. */
  entries: LaneEntry[];
  /** Bumped whenever an entry ends (the page re-reads the session records). */
  revision: number;
}

export interface LaneDeps {
  createWorker?: () => WorkerLike;
  /** 0 = apply every worker message at once (tests). */
  flushMs?: number;
  /** The upload's seam (as the runner's): the calls, the blob fetch, the timers. */
  upload?: Partial<DriverDeps>;
  /** The tab-wide queue (default: the drop runner's — one cap for the tab). */
  queue?: () => UploadQueue;
  now?: () => Date;
  /** The per-session claim (tests: null = no claim, this tab runs it). */
  claim?: (sessionId: string) => RunnerClaim | null;
  /** Tests: replaces running one entry (the queue's order is under test). */
  run?: (entry: LaneEntry, files: CollectResult | null) => Promise<void>;
  /** DI-3-S8-FE — the claims' and the yield's store (tests: two lanes, one store). */
  meta?: LaneMeta;
  /** DI-3-S8-FE — the remembered handle's permission now (no gesture). */
  handlePermission?: (handleKey: string) => Promise<HandlePermission>;
  /** DI-3-S8-FE — the folder read through the remembered handle (granted). */
  readHandle?: (handleKey: string) => Promise<CollectResult | null>;
  /** DI-3-S11-FE — the record's folder read under the granted root. */
  readUnderRoot?: (sessionId: string) => Promise<CollectResult | null>;
  /** DI-3-S16-FE — the folder picked on the card for one of the record's sets. */
  readSetFolder?: (sessionId: string) => Promise<CollectResult | null>;
  /** DI-3-S8-FE — the take-over's waits (default: a timer). */
  wait?: (ms: number) => Promise<void>;
  /**
   * DI-3-S8-FE tests: replaces the upload itself — the files check, the
   * claim, the take-over, the yield and the release stay.
   */
  send?: (entry: LaneEntry, files: CollectResult) => Promise<string | null>;
}

export const LANE_CLAIM_PREFIX = "laneClaim:";

export const LANE_WRONG_FOLDER = (ordinal: number, folder: string): string =>
  `This folder does not hold data set ${String(ordinal)} — choose the card folder ${folder} of that data set.`;
export const LANE_OTHER_TAB =
  "Another tab of this browser finishes this upload.";
/** DI-3-S8-FE — the owner's card once another tab took the upload over. */
export const LANE_TAKEN_OVER = "Taken over by another tab.";
/** DI-3-S8-FE — no files and no readable handle (or the handle's permission went). */
export const LANE_FOLDER_LOST =
  "The folder is no longer readable here — resume to choose it again.";
export const LANE_YIELD_PREFIX = "laneYield:";
/**
 * DI-3-S8-FE — how long a yielding owner waits for its in-flight requests
 * before it gives up acknowledging (and how long the stealer allows for them
 * on top of one heartbeat). DI-3-S10-FE (Christian, 2026-10-05, decision 3):
 * 30 s — one block on a slow uplink can outlast the claim's 15 s stale bound,
 * which it used to borrow.
 */
export const TAKEOVER_INFLIGHT_MS = 30_000;
/** The stealer sends nothing before the yield is acknowledged or this passed. */
export const TAKEOVER_WAIT_MS = HEARTBEAT_MS + TAKEOVER_INFLIGHT_MS;
export const TAKEOVER_POLL_MS = 250;

function laneClaimOf(sessionId: string, meta: LaneMeta): RunnerClaim {
  const key = `${LANE_CLAIM_PREFIX}${sessionId}`;
  return new RunnerClaim({
    // no channel: the claim is decided by age alone (a released claim is free)
    channel: () => null,
    read: () => meta.get<RunnerClaimRecord>(key),
    update: (fn) => meta.update<RunnerClaimRecord>(key, fn),
  });
}

/** Thrown into the driver's calls once its gate closed (a yield, a lost folder). */
class LaneStopped extends Error {
  constructor() {
    super("The lane stopped this upload.");
  }
}

/**
 * DI-3-S8-FE — one running entry's gate. Every byte, call and record write of
 * the driver passes through it; `close()` shuts it (nothing new starts) and
 * `drained()` resolves once the requests already in flight settled.
 */
class RunGate {
  private openValue = true;
  private inflight = 0;
  private readonly closers = new Set<() => void>();
  private readonly idle = new Set<() => void>();
  private resolveStop: (msg: string) => void = () => undefined;
  readonly stopped = new Promise<string>((r) => {
    this.resolveStop = r;
  });

  /** A method, not a field: it changes across an `await`. */
  isOpen(): boolean {
    return this.openValue;
  }

  close(): void {
    if (!this.openValue) return;
    this.openValue = false;
    for (const fn of this.closers) fn();
    this.closers.clear();
  }

  /** The entry's words once stopped (the race in `runEntry` reads them). */
  end(msg: string): void {
    this.resolveStop(msg);
  }

  onClose(fn: () => void): () => void {
    if (!this.openValue) {
      fn();
      return () => undefined;
    }
    this.closers.add(fn);
    return () => this.closers.delete(fn);
  }

  async track<T>(call: () => Promise<T>): Promise<T> {
    if (!this.openValue) throw new LaneStopped();
    this.inflight++;
    try {
      return await call();
    } finally {
      this.inflight--;
      if (this.inflight === 0) {
        for (const fn of this.idle) fn();
        this.idle.clear();
      }
    }
  }

  drained(): Promise<void> {
    if (this.inflight === 0) return Promise.resolve();
    return new Promise((r) => this.idle.add(r));
  }
}

export class UploadLane {
  readonly lane: StoreApi<LaneState> = createStore<LaneState>()(() => ({
    entries: [],
    revision: 0,
  }));
  /** The active entry's sets, runner-shaped (the driver's host; the cards read it). */
  readonly state: StoreApi<RunnerState> = createRunnerStore();
  readonly index: StoreApi<IndexState> = createIndexStore();
  private readonly deps: LaneDeps;
  private readonly files = new Map<string, CollectResult | null>();
  /** DI-3-S8-FE — per session: the remembered handle, and whether to watch it. */
  private readonly handles = new Map<
    string,
    { key: string | null; watch: boolean }
  >();
  /** DI-3-S8-FE — sessions whose next run steals the claim ("Take over"). */
  private readonly stealing = new Set<string>();
  private readonly approvals = new Map<string, string>();
  private active: string | null = null;
  private client: IndexClient | null = null;
  private folderFiles: readonly Blob[] = [];
  private driver: UploadDriver | null = null;
  private persistChain: Promise<unknown> = Promise.resolve();

  constructor(deps: LaneDeps = {}) {
    this.deps = deps;
  }

  private get meta(): LaneMeta {
    return this.deps.meta ?? IDB_META;
  }

  private waitMs(ms: number): Promise<void> {
    if (this.deps.wait) return this.deps.wait(ms);
    return new Promise((r) => {
      setTimeout(r, ms);
    });
  }

  private async handlePermissionOf(key: string): Promise<HandlePermission> {
    if (this.deps.handlePermission) return this.deps.handlePermission(key);
    const recalled = await recall<unknown>(key).catch(() => null);
    return recalled ? permissionOf(recalled.handle) : null;
  }

  private async readHandle(key: string): Promise<CollectResult | null> {
    if (this.deps.readHandle) return this.deps.readHandle(key);
    const recalled = await recall<DirectoryHandleLike>(key).catch(() => null);
    if (!recalled || typeof recalled.handle.values !== "function") return null;
    if ((await permissionOf(recalled.handle)) !== "granted") return null;
    return filesFromDirectoryHandle(recalled.handle).catch(() => null);
  }

  /** DI-3-S11-FE — the record's folder under the root (granted; never prompts). */
  private async readUnderRoot(
    sessionId: string,
  ): Promise<CollectResult | null> {
    if (this.deps.readUnderRoot) return this.deps.readUnderRoot(sessionId);
    const r = await rootFolderFor({ sessionId });
    if (r.kind !== "resolved") return null;
    return filesFromDirectoryHandle(r.handle).catch(() => null);
  }

  /**
   * DI-3-S16-FE — the folder picked on the card for one of the record's
   * sets (`handle:set:<daId>`, granted; never prompts).
   */
  private async readSetFolder(
    sessionId: string,
  ): Promise<CollectResult | null> {
    if (this.deps.readSetFolder) return this.deps.readSetFolder(sessionId);
    const rec = await getSession(sessionId);
    if (!rec) return null;
    const picked = await setFolderOf(rec).catch(() => null);
    if (!picked) return null;
    return filesFromDirectoryHandle(picked.handle).catch(() => null);
  }

  /** Is `sessionId` in this tab's lane (running or queued)? */
  holds(sessionId: string): boolean {
    return this.lane
      .getState()
      .entries.some((e) => e.sessionId === sessionId && e.phase !== "error");
  }

  entryOf(sessionId: string): LaneEntry | null {
    return (
      this.lane.getState().entries.find((e) => e.sessionId === sessionId) ??
      null
    );
  }

  /** The set of the RUNNING entry (live phases for the card), if it is one. */
  liveSet(sessionId: string, clusterId: string): RunnerSet | null {
    const s = this.state.getState();
    if (s.sessionId !== sessionId) return null;
    return s.sets.find((x) => x.clusterId === clusterId) ?? null;
  }

  /**
   * The card's "Resume the upload": the record's admitted sets join the lane
   * with the folder the click obtained. An entry already here (queued or
   * running) is not added twice; a stopped one is replaced. Resolves once
   * queued — the upload itself runs in turn.
   *
   * DI-3-S8-FE — only what this tab can drive (`canDrive`): without the files
   * and without a granted handle (or for another user's record) the answer is
   * `"needs_folder"` — nothing is queued and no claim is written; the card
   * asks for the folder on the click instead.
   */
  enqueue(
    rec: SessionRecord,
    admission: Extract<LaneAdmission, { kind: "admitted" }>,
    files: CollectResult | null,
    approvedAt: WireApproval,
    opts: EnqueueOptions = {},
  ): "queued" | "already" | "needs_folder" {
    const drive = canDrive(
      { handleKey: rec.handleKey, foreign: opts.foreign },
      files,
      opts.handlePermission ?? null,
      opts.underRoot === true,
      opts.setFolder === true,
    );
    if (drive === "none") return "needs_folder";
    if (this.holds(rec.sessionId)) return "already";
    for (const c of rec.clusters ?? []) {
      if (!c.dataAcquisitionId) continue;
      const at = approvedAt(c.dataAcquisitionId);
      if (typeof at === "string") this.approvals.set(c.dataAcquisitionId, at);
    }
    this.files.set(rec.sessionId, drive === "files" ? files : null);
    // DI-3-S11-FE — files held through the root: the root's permission is
    // the one to watch (the record's own handle is not granted, or none)
    const viaRoot = drive === "root" || opts.via === "root";
    // DI-3-S16-FE — files held through the set's picked folder: its permission
    const viaSet = drive === "set-folder" || opts.via === "set-folder";
    const setKey = viaSet ? (opts.setFolderKey ?? null) : null;
    this.handles.set(rec.sessionId, {
      key: viaRoot ? ROOT_KEY : viaSet ? setKey : (rec.handleKey ?? null),
      watch:
        drive === "handle" ||
        opts.via === "handle" ||
        viaRoot ||
        (viaSet && setKey !== null),
    });
    this.stealing.delete(rec.sessionId);
    const entry: LaneEntry = {
      sessionId: rec.sessionId,
      folderName: rec.folderName,
      createdAt: rec.createdAt,
      clusterIds: admission.clusterIds.slice(),
      phase: "queued",
      error: null,
      drive,
      ...(opts.auto === true ? { auto: true } : {}),
    };
    this.lane.setState((s) => ({
      entries: order([
        ...s.entries.filter((e) => e.sessionId !== rec.sessionId),
        entry,
      ]),
    }));
    void this.next();
    return "queued";
  }

  /** "Retry the upload" on a lane-held set. */
  retry(clusterId: string): void {
    this.driver?.retry(clusterId);
  }

  /**
   * DI-3-S8-FE — "Take over in this tab", on an entry that stopped on another
   * tab's claim while this tab holds the files (`canTakeOver`). The entry is
   * queued again; its run steals the claim (`RunnerClaim.takeOver`) and sends
   * nothing before the other tab yielded (see `runEntry`).
   */
  takeOver(sessionId: string): "taking-over" | "cannot" {
    const e = this.entryOf(sessionId);
    if (
      e?.phase !== "error" ||
      e.canTakeOver !== true ||
      !this.files.get(sessionId)
    )
      return "cannot";
    this.stealing.add(sessionId);
    this.lane.setState((s) => ({
      entries: order(
        s.entries.map((x) =>
          x.sessionId === sessionId
            ? { ...x, phase: "queued", error: null, canTakeOver: false }
            : x,
        ),
      ),
    }));
    void this.next();
    return "taking-over";
  }

  /** Tests / teardown. */
  destroy(): void {
    this.driver?.dispose();
    this.driver = null;
    this.client?.dispose();
    this.client = null;
    this.active = null;
    this.files.clear();
    this.handles.clear();
    this.stealing.clear();
    this.state.setState({ ...EMPTY_RUNNER });
    this.lane.setState({ entries: [], revision: 0 });
  }

  // ── internals ───────────────────────────────────────────────────────

  private patchEntry(sessionId: string, patch: Partial<LaneEntry>): void {
    this.lane.setState((s) => ({
      entries: s.entries.map((e) =>
        e.sessionId === sessionId ? { ...e, ...patch } : e,
      ),
    }));
  }

  private endEntry(sessionId: string, error: string | null): void {
    // DI-3-S8-FE — stopped on another tab's claim while this tab holds the
    // files: they stay, and the busy line offers the take-over
    // DI-3-S16-FE — an entry the card queued by itself never shows the busy
    // line nor offers the take-over: it goes, the card offers Resume again
    const silent =
      error === LANE_OTHER_TAB && this.entryOf(sessionId)?.auto === true;
    const canTakeOver =
      !silent && error === LANE_OTHER_TAB && Boolean(this.files.get(sessionId));
    if (!canTakeOver) {
      this.files.delete(sessionId);
      this.handles.delete(sessionId);
    }
    this.stealing.delete(sessionId);
    this.lane.setState((s) => ({
      entries:
        error === null || silent
          ? s.entries.filter((e) => e.sessionId !== sessionId)
          : s.entries.map((e) =>
              e.sessionId === sessionId
                ? { ...e, phase: "error", error, canTakeOver }
                : e,
            ),
      revision: s.revision + 1,
    }));
  }

  /** One entry at a time: the oldest queued one, once nothing runs. */
  private async next(): Promise<void> {
    if (this.active !== null) return;
    const entry = this.lane
      .getState()
      .entries.find((e) => e.phase === "queued");
    if (!entry) return;
    this.active = entry.sessionId;
    const files = this.files.get(entry.sessionId) ?? null;
    let error: string | null = null;
    try {
      if (this.deps.run) await this.deps.run(entry, files);
      else error = await this.runEntry(entry, files);
    } catch {
      error = "The upload could not be finished here — resume it again.";
    } finally {
      this.active = null;
      this.endEntry(entry.sessionId, error);
    }
    await this.next();
  }

  private persist<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.persistChain.then(fn, fn);
    this.persistChain = next.catch(() => undefined);
    return next;
  }

  /**
   * Resolves to null when every set's upload finished, else the card's words.
   *
   * DI-3-S8-FE — the order: (1) the files in hand — the click's, or the
   * remembered handle's read now (granted, no gesture); none → the entry ends
   * WITHOUT a claim; (2) only then the claim — or, on "Take over", the steal
   * and the wait for the other tab's yield; (3) the upload behind a gate that
   * a yield (the claim read foreign on a heartbeat) or a lost folder (the
   * handle's permission gone) closes.
   */
  private async runEntry(
    entry: LaneEntry,
    files: CollectResult | null,
  ): Promise<string | null> {
    const sid = entry.sessionId;
    const steal = this.stealing.delete(sid);
    const handle = this.handles.get(sid) ?? { key: null, watch: false };
    // (1) the files first — a tab that cannot drive the upload never claims
    let inHand = files !== null && files.files.length > 0 ? files : null;
    if (!inHand && entry.drive === "handle" && handle.key !== null)
      inHand = await this.readHandle(handle.key);
    if (!inHand && entry.drive === "set-folder")
      inHand = await this.readSetFolder(sid);
    if (!inHand && entry.drive === "root")
      inHand = await this.readUnderRoot(sid);
    if (!inHand) return LANE_FOLDER_LOST;
    this.files.set(sid, inHand); // kept for a take-over

    // (2) the claim
    const claim =
      this.deps.claim === undefined
        ? laneClaimOf(sid, this.meta)
        : this.deps.claim(sid);
    const onPageHide = () => {
      void claim?.release();
    };
    if (claim && typeof window !== "undefined")
      window.addEventListener("pagehide", onPageHide);
    const gate = new RunGate();
    let offRole: (() => void) | null = null;
    let watch: ReturnType<typeof setInterval> | null = null;
    try {
      if (claim) {
        if (steal) {
          const yieldKey = `${LANE_YIELD_PREFIX}${sid}`;
          await this.meta.delete(yieldKey).catch(() => undefined);
          const from = await claim.takeOver();
          if (from !== null) {
            this.patchEntry(sid, { phase: "taking-over" });
            await this.awaitYield(yieldKey, claim.tabId, from);
          }
          // taken back meanwhile (the other tab's own "Take over")
          if (claim.role !== "owner") return LANE_TAKEN_OVER;
        } else {
          const tabRole = await claim.claim();
          if (tabRole !== "owner") return LANE_OTHER_TAB;
        }
        // the owner's next heartbeat reads a foreign id: another tab took over
        offRole = claim.onRole((tabRole) => {
          if (tabRole === "observer")
            void this.yieldRun(gate, sid, claim, LANE_TAKEN_OVER, true);
        });
      }
      // a remembered handle whose permission goes while the lane sends
      const key = handle.watch ? handle.key : null;
      if (key !== null)
        watch = setInterval(() => {
          void this.handlePermissionOf(key).then((p) => {
            if (p === "denied" || p === "prompt" || p === null)
              void this.yieldRun(gate, sid, claim, LANE_FOLDER_LOST, false);
          });
        }, HEARTBEAT_MS);

      // (3) the upload, behind the gate
      if (this.deps.send) this.patchEntry(sid, { phase: "uploading" });
      const sent = this.deps.send
        ? this.deps.send(entry, inHand)
        : this.upload(entry, inHand, gate);
      return await Promise.race([sent, gate.stopped]);
    } finally {
      offRole?.();
      if (watch !== null) clearInterval(watch);
      gate.close();
      if (typeof window !== "undefined")
        window.removeEventListener("pagehide", onPageHide);
      this.driver?.dispose();
      this.driver = null;
      this.client?.dispose();
      this.client = null;
      this.folderFiles = [];
      // every set present: the card keeps reading the lane's last state until
      // the page re-read the record (no flash of the interrupted record); any
      // other end gives the card back to the record
      const sets = this.state.getState().sets;
      if (
        sets.length === 0 ||
        !sets.every((x) => x.upload?.phase === "present")
      )
        this.state.setState({ ...EMPTY_RUNNER });
      if (claim) {
        // an owner frees the row; a tab that yielded is an observer and
        // leaves the stealer's row alone (`release` writes only as owner)
        await claim.release();
        claim.dispose();
      }
    }
  }

  /**
   * DI-3-S8-FE — the stealer's wait: until the owner acknowledged its yield
   * (`laneYield:<sid>` naming both tabs) or `TAKEOVER_WAIT_MS` passed (one
   * heartbeat for the owner to notice + the in-flight bound — an owner that
   * does not answer by then is gone).
   */
  private async awaitYield(
    key: string,
    me: string,
    from: string,
  ): Promise<boolean> {
    const t0 = Date.now();
    for (;;) {
      const ack = await this.meta.get<LaneYield>(key).catch(() => undefined);
      if (ack?.to === me && ack.from === from) {
        await this.meta.delete(key).catch(() => undefined);
        return true;
      }
      if (Date.now() - t0 >= TAKEOVER_WAIT_MS) return false;
      await this.waitMs(TAKEOVER_POLL_MS);
    }
  }

  /**
   * DI-3-S8-FE — stop the running entry: close the gate (no new block, call
   * or record write from here on), stop the driver, let the requests in
   * flight settle (bounded by `TAKEOVER_INFLIGHT_MS`), then — on a take-over —
   * acknowledge to the tab that holds the claim now, and end the entry.
   */
  private async yieldRun(
    gate: RunGate,
    sid: string,
    claim: RunnerClaim | null,
    msg: string,
    ack: boolean,
  ): Promise<void> {
    if (!gate.isOpen()) return;
    gate.close();
    this.driver?.dispose();
    const settled = await Promise.race([
      gate
        .drained()
        .then(() => this.persistChain)
        .then(() => true),
      this.waitMs(TAKEOVER_INFLIGHT_MS).then(() => false),
    ]);
    if (ack && settled && claim) {
      const row = await this.meta
        .get<RunnerClaimRecord>(`${LANE_CLAIM_PREFIX}${sid}`)
        .catch(() => undefined);
      if (row && row.tabId !== claim.tabId) {
        const value: LaneYield = {
          from: claim.tabId,
          to: row.tabId,
          at: Date.now(),
        };
        await this.meta
          .set(`${LANE_YIELD_PREFIX}${sid}`, value)
          .catch(() => undefined);
      }
    }
    gate.end(msg);
  }

  private async upload(
    entry: LaneEntry,
    files: CollectResult,
    gate: RunGate,
  ): Promise<string | null> {
    // DI-3-S8-FE — every write of the record goes through the gate
    const persist = <T>(fn: () => Promise<T>): Promise<T> =>
      gate.isOpen() ? this.persist(fn) : Promise.resolve(undefined as T);
    const sid = entry.sessionId;
    const rec = await getSession(sid);
    if (!rec) return "This session is no longer in this browser.";
    const now = (this.deps.now ?? (() => new Date()))();
    const wanted = new Set(entry.clusterIds);
    const sets: RunnerSet[] = [];
    (rec.clusters ?? []).forEach((c, k) => {
      if (!wanted.has(c.clusterId)) return;
      const r = runnerSetFromCluster(c, k);
      if (!r?.upload) return;
      let upload = r.upload;
      // the approval is the WIRE's (admission); the record only bridges it:
      // an "after verification" set starts once `approvedAt` is there
      const wire = c.dataAcquisitionId
        ? this.approvals.get(c.dataAcquisitionId)
        : undefined;
      if (!upload.approvedAt && wire) upload = { ...upload, approvedAt: wire };
      // a failed upload starts again, as "Retry the upload" does
      if (upload.phase === "failed")
        upload = {
          ...upload,
          phase: "queued",
          pass: upload.pass + 1,
          sessionId: null,
          jobId: null,
          error: null,
          retryAt: null,
          finishedAt: null,
        };
      // the record's last pass may carry a stale transport stop
      if (upload.error !== null || upload.retryAt !== null)
        upload = { ...upload, error: null, retryAt: null };
      sets.push({ ...r, upload });
    });
    if (sets.length === 0) return null;
    this.state.setState({
      ...EMPTY_RUNNER,
      sessionId: sid,
      folderName: rec.folderName,
      started: true,
      startedAt: now.toISOString(),
      sets,
    });
    for (const s of sets) {
      const upload = s.upload;
      if (upload)
        await persist(() => updateCluster(sid, s.clusterId, { upload }));
    }

    if (!gate.isOpen()) return gate.stopped;
    this.patchEntry(sid, { phase: "reading" });
    const missing = await this.readFolder(rec, files, gate);
    if (!gate.isOpen()) return gate.stopped;
    if (missing !== null) return LANE_WRONG_FOLDER(missing, rec.folderName);
    this.patchEntry(sid, { phase: "uploading" });
    const driver = new UploadDriver(
      {
        state: this.state,
        index: this.index,
        // DI-3-S8-FE — no file leaves this tab once the gate closed
        fileOf: (i) => (gate.isOpen() ? (this.folderFiles[i] ?? null) : null),
        persist,
        now: this.deps.now ?? (() => new Date()),
      },
      {
        ...(this.deps.upload ?? {}),
        ...gatedTransport(gate, this.deps.upload),
        queue: (this.deps.queue ?? (() => intakeRunner.uploads.queue))(),
      },
    );
    this.driver = driver;
    await new Promise<void>((resolve) => {
      const done = () =>
        this.state.getState().sets.every((s) => uploadFinished(s.upload));
      if (done()) {
        resolve();
        return;
      }
      const off = this.state.subscribe(() => {
        if (!done()) return;
        off();
        offClose();
        resolve();
      });
      const offClose = gate.onClose(() => {
        off();
        resolve();
      });
      driver.pump();
    });
    if (!gate.isOpen()) return gate.stopped;
    await this.persistChain;
    // M5-NODE-01 — an escape-hatch record is done once its upload is
    // present: it ends (DI-3-P6's mark) — never resumed, never offered again
    if (
      rec.escapeHatch &&
      this.state.getState().sets.every((x) => x.upload?.phase === "present")
    )
      await persist(() =>
        updateSession(sid, (r) => ({
          ...r,
          endedAt: (this.deps.now ?? (() => new Date()))().toISOString(),
        })),
      );
    return null;
  }

  /**
   * Re-index the folder with the hashes persisted at the round trip, then
   * give every lane set its members back by its stable id. Resolves to null,
   * or the ordinal of a set the folder does not hold.
   *
   * DI-3-S15-FE (smoke R2-F3) — the persisted hashes are found by file name
   * + size (`knownHashesOf` / `knownForFolder`, `parse/hashKey.ts`), so a set
   * dropped as loose files keeps every hash when its folder is picked. The
   * verdict is taken only once the files it rests on are hashed: a set the
   * stable id does not find is looked up by its persisted hashes, and the
   * folder "does not hold" it only when one of them is absent from it.
   */
  private async readFolder(
    rec: SessionRecord,
    result: CollectResult,
    gate: RunGate,
  ): Promise<number | null> {
    const known = knownForFolder(
      knownHashesOf((rec.clusters ?? []).map((c) => c.rowHashes)),
      result.files.map((f) => ({
        relativePath: f.relativePath,
        size: f.file.size,
      })),
    );
    this.folderFiles = result.files.map((f) => f.file);
    this.index.getState().load({
      sessionId: rec.sessionId,
      folderName: result.folderName,
      files: result.files.map((f) => ({
        name: f.file.name,
        relativePath: f.relativePath,
        size: f.file.size,
      })),
      skipped: result.skipped,
    });
    const client = new IndexClient({
      store: this.index,
      ...(this.deps.createWorker
        ? { createWorker: this.deps.createWorker }
        : {}),
      ...(this.deps.flushMs !== undefined
        ? { flushMs: this.deps.flushMs }
        : {}),
    });
    this.client = client;
    await new Promise<void>((resolve) => {
      const parsed = () => {
        const s = this.index.getState();
        return (
          s.phase === "error" ||
          s.phase === "cancelled" ||
          (s.total > 0 && s.parsedCount + s.errors.length >= s.total)
        );
      };
      const off = this.index.subscribe(() => {
        if (!parsed()) return;
        off();
        offClose();
        resolve();
      });
      const offClose = gate.onClose(() => {
        off();
        resolve();
      });
      if (result.files.length === 0) {
        off();
        offClose();
        resolve();
        return;
      }
      client.start(
        rec.sessionId,
        result.files.map((f) => ({
          file: f.file,
          name: f.file.name,
          size: f.file.size,
          relativePath: f.relativePath,
        })),
        { knownHashes: known },
      );
    });
    const parsedRows = this.index.getState().rows;
    const byId = new Map(
      proposalSets(groupForDecide(parsedRows), parsedRows).map(
        (p) => [p.clusterId, p] as const,
      ),
    );
    // the members each set will have — by its stable id (or, for an add, the
    // whole folder); a set the id does not find rests on EVERY file's hash
    const sets = this.state.getState().sets;
    const found = new Map<string, number[]>();
    let byHash = false;
    for (const s of sets) {
      const members = s.boundTo
        ? folderMembers(parsedRows)
        : byId.get(s.clusterId)?.memberIndexes.slice();
      if (members) found.set(s.clusterId, members);
      else byHash = true;
    }
    // the members' hashes (the upload's names and MD5s): a file the round
    // trip hashed reports them right after its parse (DI-3-P2); any other is
    // hashed as on a first drop. Neither the verdict nor the upload starts
    // before every file they rest on has one — `uploadFilesOf` would
    // otherwise leave an unhashed file out, and a slow hash must never read
    // as "wrong folder" (DI-3-S15-FE).
    const needed = new Set<number>(
      byHash ? parsedRows.map((r) => r.index) : [...found.values()].flat(),
    );
    await new Promise<void>((resolve) => {
      const hashed = () => {
        const s = this.index.getState();
        if (s.phase === "error" || s.phase === "cancelled") return true;
        for (const i of needed) {
          const r = s.rows[i];
          if (r && !r.hashes && !r.error) return false;
        }
        return true;
      };
      if (hashed() || !gate.isOpen()) {
        resolve();
        return;
      }
      const off = this.index.subscribe(() => {
        if (!hashed()) return;
        off();
        offClose();
        resolve();
      });
      const offClose = gate.onClose(() => {
        off();
        resolve();
      });
    });
    // the verdict — after hashing
    const rows = this.index.getState().rows;
    for (const s of sets) {
      if (s.boundTo) {
        // DI-3-G — an add owns the whole folder (its own session)
        const members = found.get(s.clusterId) ?? [];
        if (members.length === 0) return s.ordinal;
        const keys = s.uploadKeys ? new Set(s.uploadKeys) : null;
        let bytes = 0;
        for (const i of members) {
          const r = rows[i];
          if (r && (!keys || (r.hashes && keys.has(fileKey(r.hashes)))))
            bytes += r.size;
        }
        patchSet(this.state, s.clusterId, {
          memberIndexes: members,
          sizeBytes: bytes,
        });
        continue;
      }
      const p = byId.get(s.clusterId);
      if (p) {
        patchSet(this.state, s.clusterId, {
          memberIndexes: p.memberIndexes.slice(),
          sizeBytes: p.sizeBytes,
        });
        continue;
      }
      const held = membersByHash(
        rows,
        (rec.clusters ?? []).find((c) => c.clusterId === s.clusterId)
          ?.rowHashes,
      );
      if (!held) return s.ordinal;
      patchSet(this.state, s.clusterId, held);
    }
    return null;
  }
}

/**
 * DI-3-S15-FE — a set the stable id does not find in the folder (the folder
 * holds more images, or its earliest image is not read the same): its
 * members are the rows whose hash is one the set's round trip persisted.
 * Null — the folder does not hold the set — when the set persisted no hash
 * or any one of its hashes is absent from the folder.
 */
function membersByHash(
  rows: readonly IndexRow[],
  persisted: Record<string, HashResult> | undefined,
): { memberIndexes: number[]; sizeBytes: number } | null {
  const wanted = new Set(Object.values(persisted ?? {}).map(fileKey));
  if (wanted.size === 0) return null;
  const seen = new Set<string>();
  const memberIndexes: number[] = [];
  let sizeBytes = 0;
  for (const r of rows) {
    if (!r.hashes) continue;
    const k = fileKey(r.hashes);
    if (!wanted.has(k)) continue;
    seen.add(k);
    memberIndexes.push(r.index);
    sizeBytes += r.size;
  }
  return seen.size === wanted.size ? { memberIndexes, sizeBytes } : null;
}

/**
 * DI-3-S8-FE — the driver's transport behind the gate: every blob PUT
 * (`fetch`) and every upload-session call (`api`) is counted while in flight
 * and refused once the gate closed; the back-off sleeps end at once then, so
 * the driver's loop runs out without sending.
 */
function gatedTransport(
  gate: RunGate,
  deps: Partial<DriverDeps> | undefined,
): { api: UploadApi; fetch: FetchLike; sleep: (ms: number) => Promise<void> } {
  const api = deps?.api ?? REAL_UPLOAD_API;
  const doFetch: FetchLike = deps?.fetch ?? ((u, i) => fetch(u, i));
  const sleep = deps?.sleep;
  return {
    api: {
      open: (...a) => gate.track(() => api.open(...a)),
      complete: (...a) => gate.track(() => api.complete(...a)),
      poll: (...a) => gate.track(() => api.poll(...a)),
      withhold: (...a) => gate.track(() => api.withhold(...a)),
      status: (...a) => gate.track(() => api.status(...a)),
      // DI-3-S9 — the resume's read of the names the server knows missing
      missingBlobs: (...a) => gate.track(() => api.missingBlobs(...a)),
    },
    fetch: (u, i) => gate.track(() => doFetch(u, i)),
    sleep: (ms) => {
      if (!gate.isOpen()) return Promise.resolve();
      if (sleep) return sleep(ms);
      return new Promise((r) => {
        const t = setTimeout(done, ms);
        const off = gate.onClose(done);
        function done() {
          clearTimeout(t);
          off();
          r();
        }
      });
    },
  };
}

/** The running / reading entry first, then the queue by the record's creation time. */
function order(entries: readonly LaneEntry[]): LaneEntry[] {
  const rank = (e: LaneEntry) => (e.phase === "queued" ? 1 : 0);
  return [...entries].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0),
  );
}

/** The tab's lane — created with the module, alive for the SPA tab. */
export const uploadLane = new UploadLane();

export function useLaneState<T>(
  selector: (s: LaneState) => T,
  lane: UploadLane = uploadLane,
): T {
  return useStore(lane.lane, selector);
}

export function useLaneSets(lane: UploadLane = uploadLane): RunnerState {
  return useStore(lane.state, (s) => s);
}

/**
 * Is `sessionId` held by an upload lane of this browser — this tab's, or a
 * fresh lane claim of another tab? Then no card, no `?da=` view and no
 * resume may make it the active session (the drop slot and the lane would
 * upload the same set twice).
 */
export async function laneBusy(
  sessionId: string,
  lane: UploadLane = uploadLane,
  now: number = Date.now(),
): Promise<boolean> {
  if (lane.holds(sessionId)) return true;
  const cur = await getMeta<RunnerClaimRecord>(
    `${LANE_CLAIM_PREFIX}${sessionId}`,
  ).catch(() => undefined);
  return cur !== undefined && cur.at > 0 && !claimable(cur, "", now);
}

/**
 * DI-3-S16-FE — a fresh lane claim on `sessionId` (another tab sends its
 * upload): the claim row, else null. A read only — the automatic start stays
 * silent while it is fresh and never takes it over; a claim of
 * `STALE_MS` age is free by the claim protocol (DI-3-S4-FE / S8).
 */
export async function laneClaimElsewhere(
  sessionId: string,
  now: number = Date.now(),
  meta: Pick<LaneMeta, "get"> = IDB_META,
): Promise<RunnerClaimRecord | null> {
  const cur = await meta
    .get<RunnerClaimRecord>(`${LANE_CLAIM_PREFIX}${sessionId}`)
    .catch(() => undefined);
  return cur !== undefined && cur.at > 0 && !claimable(cur, "", now)
    ? cur
    : null;
}
